package com.tixforgigs.common.core.util

import com.tixforgigs.apax.models.*
import com.tixforgigs.common.core.annotations.MissingTaramiMessageKey
import com.tixforgigs.common.core.enums.View
import com.tixforgigs.common.core.extensions.isNotNull
import com.tixforgigs.common.core.extensions.isNull
import com.tixforgigs.common.core.extensions.orElse
import com.tixforgigs.common.core.extensions.toLocale
import com.tixforgigs.common.core.util.LocalizationUtil.currentPaymentCountry
import com.tixforgigs.common.core.util.StorageUtil.deleteStorageItem
import com.tixforgigs.common.core.util.StorageUtil.getStorageItem
import com.tixforgigs.common.core.util.StorageUtil.setStorageItem
import com.tixforgigs.common.core.util.ToastUtil.showToast
import com.tixforgigs.common.http.ApiGetPath
import com.tixforgigs.common.http.ApiPostPath
import com.tixforgigs.common.http.HttpClient.BASE_URL
import com.tixforgigs.common.http.HttpClient.apiV4Get
import com.tixforgigs.common.http.HttpClient.apiV4Post
import com.tixforgigs.tarami.GlobalMessage.textToastMessagePaymentApiError
import com.tixforgigs.tarami.GlobalMessage.textToastMessagePaymentError
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.withContext
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlin.time.ExperimentalTime


object PaymentUtil {

  const val PAYPAL_WRAPPER_ID = "xm-paypal-payment"
  //const val PAYMENT_SECTION_TOP_ID = "xm-payment-section"

  const val PAYMENT_WIDGET_ID = "xm-payment-widget"
  const val PAYMENT_WRAPPER_ID = "xm-payment-wrapper"


  @OptIn(ExperimentalTime::class)
  suspend fun createOrder(
    cart: CartResponse,
    userData: OrderUserData,
    invoiceAddress: Address? = null,
    shippingAddress: Address? = null,
    //shippingMethod: ShippingMethod? = null,
    onStarted: () -> Unit = {},
    onError: (Throwable?) -> Unit = {},
    onSuccess: () -> Unit = {},
  ): OrderResponse? = withContext(Dispatchers.Default) {
    apiV4Post<OrderResponse>(
      type = ApiPostPath.OrderCreate(
        body = OrderCreateRequest(
          channel = PlatformChannel.WEB,
          userData = userData,
          invoiceAddress = invoiceAddress,
          // TODO insert shipping address from user input
          shippingAddress = shippingAddress,
          locale = currentPaymentCountry.value.inputValue.toLocale(),
          paymentTypeId = PaymentTypeId.UNDEFINED,
          shoppingCart = cart.shoppingCart,
          redirectUrl = "${BASE_URL}${View.PAYMENT_REDIRECT(null, null).path}",
          //shippingMethod = shippingMethod
        )
      ),
      onStart = onStarted,
      onCompletionWithError = { error, apiError, oauthError, _ ->
        onCompletionError(error, apiError, oauthError, onError)
      },
      onCompletionSuccess = {
        onSuccess()
      }
    ).also { it.storeTemporarily() }
  }


  @OptIn(ExperimentalTime::class)
  suspend fun restartOrder(
    orderId: Int,
    orderToken: String,
    onStarted: () -> Unit = {},
    onError: (Throwable?) -> Unit = {},
    onSuccess: () -> Unit = {},
  ): OrderResponse? = withContext(Dispatchers.Default) {
    apiV4Post<OrderResponse>(
      type = ApiPostPath.OrderRestart(
        body = OrderRestartRequest(
          paymentTypeId = PaymentTypeId.UNDEFINED,
          channel = PlatformChannel.WEB,
          redirectUrl = "${BASE_URL}${View.PAYMENT_REDIRECT(null, null).path}",
        ),
        orderId = orderId,
        orderToken = orderToken,
      ),
      onStart = onStarted,
      onCompletionWithError = { error, apiError, oauthError, _ ->
        onCompletionError(error, apiError, oauthError, onError)
      },
      onCompletionSuccess = {
        onSuccess()
      }
    ).also { it.storeTemporarily() }
  }


  suspend fun onOrderApproved(
    orderId: Int,
    orderToken: String,
    paymentTypeId: PaymentTypeId? = null,
    onSuccess: () -> Unit,
    onError: (Throwable?, ApiError?, OAuthError?) -> Unit,
  ) = withContext(Dispatchers.Default) {
    apiV4Post<OrderResponse>(
      type = ApiPostPath.OrderApprove(
        orderId = orderId,
        orderToken = orderToken,
        body = OrderApproveRequest(
          channel = PlatformChannel.WEB,
          paymentTypeId = paymentTypeId
        )
      ),
      onCompletionWithError = { error, apiError, oauthError, _ ->
        onError(error, apiError, oauthError)
      },
      onCompletionSuccess = {
        setLatestOrder()
        deleteUnresolvedOrderLocally()
        deleteCartLocally()
        onSuccess()
      }
    )
  }

  @OptIn(ExperimentalTime::class)
  suspend fun unresolvedOrderExpiredChecker() {
    while (getStorageItem<OrderResponse>(StorageUtil.StorageKey.UnresolvedOrderMetadata).isNotNull) {
      val orderResponse = getStorageItem<OrderResponse>(StorageUtil.StorageKey.UnresolvedOrderMetadata)
      val remainingSec = (orderResponse?.orderExpires.orElse { Clock.System.now() } - Clock.System.now()).inWholeSeconds
      if (remainingSec <= 0L) {
        deleteUnresolvedOrderLocally()
      }
      delay(1.seconds)
    }
  }

  @OptIn(ExperimentalTime::class)
  suspend fun latestOrderExpiredChecker() {
    while (
      getStorageItem<Order>(
        key = StorageUtil.StorageKey.LatestCompletedOrder,
        storageType = StorageUtil.StorageType.SESSION_STORAGE
      ).isNotNull
    ) {
      getStorageItem<Order>(
        key = StorageUtil.StorageKey.LatestCompletedOrder,
        storageType = StorageUtil.StorageType.SESSION_STORAGE
      )?.let { order ->
        val latestEndDateOfOrder = order.events.orEmpty().map {
          it.endsAt.orElse {
            it.beginsAt.plus(1, DateTimeUnit.DAY, TimeZone.currentSystemDefault())
          }
        }.maxBy { it.toEpochMilliseconds() }
        val remainingSec = (latestEndDateOfOrder - Clock.System.now()).inWholeSeconds
        if (remainingSec <= 0L) {
          deleteLatestCompletedLocally()
        }
        delay(1.minutes)
      }
    }
  }

  fun deleteUnresolvedOrderLocally() {
    deleteStorageItem(StorageUtil.StorageKey.UnresolvedOrderMetadata)
    deleteStorageItem(StorageUtil.StorageKey.UnresolvedOrder)
  }

  fun deleteCartLocally() {
    deleteStorageItem(StorageUtil.StorageKey.ShoppingCart)
  }

  fun deleteLatestCompletedLocally() {
    deleteStorageItem(
      key = StorageUtil.StorageKey.LatestCompletedOrder,
      storageType = StorageUtil.StorageType.SESSION_STORAGE
    )
  }

  fun setLatestOrder() {
    getStorageItem<Order>(StorageUtil.StorageKey.UnresolvedOrder)?.let { order ->
      setStorageItem(
        key = StorageUtil.StorageKey.LatestCompletedOrder,
        item = order,
        serializer = Order.serializer(),
        storageType = StorageUtil.StorageType.SESSION_STORAGE
      )
    }
  }

  private fun onCompletionError(
    error: Throwable?,
    apiError: ApiError?,
    oauthError: OAuthError?,
    onError: (error: Throwable?) -> Unit = {},
  ) {
    if (apiError != null) {
      showToast(
        message = textToastMessagePaymentApiError(
          p3 = apiError.status,
          p2 = apiError.title.toString(),
          p1 = apiError.detail.toString(),
          p0 = apiError.properties?.toList()?.joinToString { "${it.first} = ${it.second}" }.toString()
        ),
        type = ToastUtil.ToastType.ERROR,
        options = toastOptions { autoClose = 10.seconds.inWholeMilliseconds }
      )
    }
    if (oauthError != null) {
      @MissingTaramiMessageKey
      showToast(
        message = oauthError.error.value,
        type = ToastUtil.ToastType.ERROR,
        options = toastOptions { autoClose = 10.seconds.inWholeMilliseconds }
      )
    }
    if (apiError.isNull && oauthError.isNull && error != null) {
      showToast(
        message = textToastMessagePaymentError(error.message.toString()),
        type = ToastUtil.ToastType.ERROR,
        options = toastOptions { autoClose = 10.seconds.inWholeMilliseconds }
      )
    }
    onError(error)
  }

  private suspend fun OrderResponse?.storeTemporarily() {
    this?.let {
      deleteStorageItem(StorageUtil.StorageKey.ShoppingCart)
      setStorageItem<OrderResponse>(
        StorageUtil.StorageKey.UnresolvedOrderMetadata,
        it,
        OrderResponse.serializer()
      )
      apiV4Get<Order>(
        type = ApiGetPath.OrderById(
          orderId = it.orderId,
          orderToken = it.orderToken
        )
      )?.let { order ->
        setStorageItem<Order>(
          StorageUtil.StorageKey.UnresolvedOrder,
          order,
          Order.serializer()
        )
      }
    }
  }

}
